import type { KnowledgeSyncProviderType } from '@terra/shared-contracts-knowledge-sync';

export type KnowledgeSourceScope = {
  scopeType: 'CONTAINER' | 'ITEM';
  scopeExternalId: string;
};

export type KnowledgeSourceScopeCounts = {
  containerCount: number;
  itemCount: number;
};

export type KnowledgeSourceScopeSummaryKey =
  | 'scopeSummary.spaces'
  | 'scopeSummary.databases'
  | 'scopeSummary.folders'
  | 'scopeSummary.pages'
  | 'scopeSummary.files';

export type KnowledgeSourceScopeSummaryKeys = {
  container: KnowledgeSourceScopeSummaryKey;
  item: KnowledgeSourceScopeSummaryKey;
};

const NOTION_PROVIDER: KnowledgeSyncProviderType = 'notion';
const NOTION_PAGE_SCOPE_PREFIX = 'page:';

const SCOPE_SUMMARY_KEYS: Record<
  KnowledgeSyncProviderType,
  KnowledgeSourceScopeSummaryKeys
> = {
  confluence: {
    container: 'scopeSummary.spaces',
    item: 'scopeSummary.pages',
  },
  notion: {
    container: 'scopeSummary.databases',
    item: 'scopeSummary.pages',
  },
  google_drive: {
    container: 'scopeSummary.folders',
    item: 'scopeSummary.files',
  },
  box: {
    container: 'scopeSummary.folders',
    item: 'scopeSummary.files',
  },
};

const isNotionPageScope = (scope: KnowledgeSourceScope) =>
  scope.scopeType === 'ITEM' ||
  (scope.scopeType === 'CONTAINER' &&
    scope.scopeExternalId.startsWith(NOTION_PAGE_SCOPE_PREFIX));

const isNotionDatabaseScope = (scope: KnowledgeSourceScope) =>
  scope.scopeType === 'CONTAINER' &&
  !scope.scopeExternalId.startsWith(NOTION_PAGE_SCOPE_PREFIX);

export const getKnowledgeSourceScopeCounts = ({
  provider,
  scopes,
}: {
  provider: KnowledgeSyncProviderType;
  scopes: KnowledgeSourceScope[];
}): KnowledgeSourceScopeCounts => {
  if (provider === NOTION_PROVIDER) {
    return {
      containerCount: scopes.filter(isNotionDatabaseScope).length,
      itemCount: scopes.filter(isNotionPageScope).length,
    };
  }

  return {
    containerCount: scopes.filter((scope) => scope.scopeType === 'CONTAINER')
      .length,
    itemCount: scopes.filter((scope) => scope.scopeType === 'ITEM').length,
  };
};

export const getKnowledgeSourceScopeSummaryKeys = (
  provider: KnowledgeSyncProviderType,
): KnowledgeSourceScopeSummaryKeys => SCOPE_SUMMARY_KEYS[provider];

export const formatKnowledgeSourceScopeSummary = ({
  counts,
  keys,
  formatCount,
  joinParts,
}: {
  counts: KnowledgeSourceScopeCounts;
  keys: KnowledgeSourceScopeSummaryKeys;
  formatCount: ({
    key,
    count,
  }: {
    key: KnowledgeSourceScopeSummaryKey;
    count: number;
  }) => string;
  joinParts: ({
    containers,
    items,
  }: {
    containers: string;
    items: string;
  }) => string;
}): string => {
  const containers =
    counts.containerCount > 0
      ? formatCount({ key: keys.container, count: counts.containerCount })
      : null;
  const items =
    counts.itemCount > 0
      ? formatCount({ key: keys.item, count: counts.itemCount })
      : null;

  if (containers && items) {
    return joinParts({ containers, items });
  }

  return containers ?? items ?? '';
};
